'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { set16ToRgba } from '@/utils/tools';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePinkLeopardTracking } from '../hooks/usePinkLeopardTracking';

interface ColorListsProps {
  productListsTwo: Array<{
    shop_sku?: string;
    business_sku?: string;
    sku?: string;
    name?: string;
    short_name?: string;
    img_word?: string;
    btn_word?: string;
    leg_img?: string;
    frame_img?: string;
    color_arr?: Array<{
      colorImage?: string;
      bgColor?: string;
      bgColor1?: string;
      transparency?: string;
      transparency1?: string;
      isSolid?: boolean;
    }>;
    chooseIndex?: number;
    price_info?: any;
  }>;
}

// PC端专用的ColorLists组件 - 对应Vue版本的ColorLists.vue
export default function ColorLists({ productListsTwo }: ColorListsProps) {
  const { trackProductPitsExposure, trackProductPitsClick, trackBuyNowClick, trackColorClick } =
    usePinkLeopardTracking();

  const { navigate } = useAppNavigation();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  // 处理颜色选择的本地状态
  const [colorSelections, setColorSelections] = useState<{ [key: number]: number }>({});

  // 曝光状态管理 - 使用useRef避免重新渲染
  const exposedItems = useRef<Set<number>>(new Set());

  // 获取商品价格信息
  const getPriceInfo = useCallback(
    (item: any) => {
      return priceInfo(userInfo, item, currency);
    },
    [userInfo, currency],
  );

  // 处理商品跳转 - 统一方法名
  const handleProductClick = useCallback(
    (product: any, index: number) => {
      trackProductPitsClick(product, index, '商品位2');
      if (product.shop_sku) {
        navigate(`/goods-detail/${product.shop_sku}`);
      }
    },
    [trackProductPitsClick, navigate],
  );

  // 处理购买按钮点击
  const handleBuyNowClick = useCallback(
    (e: React.MouseEvent, product: any, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      trackBuyNowClick(product, index, '商品位2');
      if (product.shop_sku) {
        navigate(`/goods-detail/${product.shop_sku}`);
      }
    },
    [trackBuyNowClick, navigate],
  );

  // 处理颜色选择
  const handleColorChange = (
    e: React.MouseEvent,
    colorItem: any,
    colorIndex: number,
    productIndex: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // 如果颜色没有变化，不触发更新
    if (colorSelections[productIndex] === colorIndex) {
      return;
    }

    setColorSelections((prev) => ({
      ...prev,
      [productIndex]: colorIndex,
    }));

    // 触发埋点
    trackColorClick(colorItem, productIndex, '商品位2');
  };

  // 计算选中的颜色 - 使用useMemo避免频繁重新计算
  const getChosenColor = useCallback(
    (product: any, productIndex: number) => {
      const index = colorSelections[productIndex] ?? product.chooseIndex ?? 0;
      const colorItem = product.color_arr?.[index];

      if (!colorItem) return 'transparent';

      try {
        if (colorItem.isSolid) {
          return set16ToRgba(colorItem.bgColor, colorItem.transparency);
        } else {
          return `linear-gradient(${set16ToRgba(colorItem.bgColor, colorItem.transparency)}, ${set16ToRgba(colorItem.bgColor1, colorItem.transparency1)})`;
        }
      } catch {
        return 'transparent';
      }
    },
    [colorSelections],
  );

  // 商品曝光组件 - 每个商品单独监听曝光
  const ProductItem = React.memo(({ product, index }: { product: any; index: number }) => {
    const { ref, inView } = useInView({
      threshold: 0.8,
      triggerOnce: true, // 性能优化：只触发一次
      rootMargin: '0px 0px -10% 0px', // 性能优化：提前10%触发
    });

    useEffect(() => {
      if (inView && !exposedItems.current.has(index)) {
        // 延迟上报，减少滚动时的性能冲击
        const timer = setTimeout(() => {
          trackProductPitsExposure(product, index, '商品位2');
          exposedItems.current.add(index);
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [inView, product, index]);

    // 使用useMemo缓存计算结果，避免颜色切换时重新计算
    const chosenColor = useMemo(() => {
      return getChosenColor(product, index);
    }, [getChosenColor, product, index]);

    const productPriceInfo = useMemo(() => {
      return getPriceInfo(product);
    }, [getPriceInfo, product]);

    return (
      <li ref={ref} key={index} className="w-[32.6041666667vw]">
        {/* 商品图片和颜色选择区域 */}
        <a
          href={`/goods-detail/${product.shop_sku}`}
          aria-label={product.short_name}
          onClick={(e) => {
            e.preventDefault();
            handleProductClick(product, index);
          }}
          className="cursor-pointer block"
        >
          <div
            className="img-wrap w-full bg-[#F2F3F4] 
                md:py-[60px]
                lg:py-[80px]
                xl:py-[100px]
                2xl:py-[108px]
                3xl:py-[120px]"
          >
            {/* 镜片图像叠加区域 */}
            <div className="frame_bg_wrap relative mx-auto w-[29.1666666667vw] h-[14.5833333333vw]">
              {/* 镜腿背景 - z-index 8 */}
              <Image
                src={product.leg_img}
                alt={product.name}
                className="leg_bg absolute left-0 top-0 block w-full h-auto z-[8] contrast-90"
                width={0}
                height={0}
                sizes="30vw"
                style={{ width: '100%', height: 'auto' }}
              />

              {/* 镜片颜色叠加 - z-index 9 */}
              <div
                className="lens_bg_color aspect-[2/1] relative z-[9] w-full h-auto"
                style={{ background: chosenColor }}
              />

              {/* 镜框前景 - z-index 10 */}
              <Image
                src={product.frame_img}
                alt={product.name}
                className="frame_bg absolute left-0 top-0 block w-full h-auto z-[10] contrast-90"
                width={0}
                height={0}
                sizes="30vw"
                style={{ width: '100%', height: 'auto' }}
              />
            </div>

            {/* 商品文字和颜色选择器 */}
            <div className="type-word">
              <p
                className="text-center mx-0
                    md:mt-[15px] md:mb-[5px] md:text-[12px]
                    lg:mt-[20px] lg:mb-[7px] lg:text-[13px]
                    xl:mt-[25px] xl:mb-[8px] xl:text-[17px]
                    2xl:mt-[27px] 2xl:mb-[9px] 2xl:text-[18px]
                    3xl:mt-[30px] 3xl:mb-[10px] 3xl:text-[20px]"
              >
                {product.img_word}
              </p>

              {/* 颜色选择器 */}
              <ul className="color-list flex justify-center items-center">
                {product.color_arr?.map((colorItem, colorIndex) => (
                  <li
                    key={colorIndex}
                    className={`
                          md:w-[15px] md:h-[15px]
                          lg:w-[20px] lg:h-[20px]
                          xl:w-[25px] xl:h-[25px]
                          2xl:w-[27px] 2xl:h-[27px]
                          3xl:w-[30px] 3xl:h-[30px]
                          rounded-full mx-[5px] cursor-pointer box-border
                          ${
                            (colorSelections[index] || product.chooseIndex || 0) === colorIndex
                              ? 'border border-[#979797]'
                              : ''
                          }`}
                    onClick={(e) => handleColorChange(e, colorItem, colorIndex, index)}
                  >
                    <Image
                      src={colorItem.colorImage}
                      alt={colorItem.bgColor}
                      className="w-full h-auto rounded-full"
                      width={30}
                      height={30}
                      sizes="30px"
                    />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </a>

        {/* 底部商品信息和购买按钮 */}
        <div
          className="info-wrap flex justify-between items-center
              md:mx-0 md:mt-[10px] md:mb-[30px] md:ml-[10px]
              lg:mx-0 lg:mt-[13px] lg:mb-[40px] lg:ml-[13px]
              xl:mx-0 xl:mt-[17px] xl:mb-[50px] xl:ml-[17px]
              2xl:mx-0 2xl:mt-[18px] 2xl:mb-[54px] 2xl:ml-[18px]
              3xl:mx-0 3xl:mt-[20px] 3xl:mb-[60px] 3xl:ml-[20px]"
        >
          <div className="left-part">
            <p
              className="
                  md:mb-[5px] md:text-[12px]
                  lg:mb-[7px] lg:text-[13px]
                  xl:mb-[8px] xl:text-[17px]
                  2xl:mb-[9px] 2xl:text-[18px]
                  3xl:mb-[10px] 3xl:text-[20px]"
            >
              {product.short_name}
            </p>
            <div className="price-part">
              <span
                className="now-price notranslate font-bb
                    md:text-[14px]
                    lg:text-[15px]
                    xl:text-[18px]
                    2xl:text-[20px]
                    3xl:text-[22px]"
              >
                {formatPriceNumberWithSymbol(productPriceInfo.sell_price, currency)}
              </span>
              {productPriceInfo.sell_price !== productPriceInfo.price && (
                <span
                  className="old-price notranslate text-[#A9A9A9] line-through
                      md:ml-[5px] md:text-[12px]
                      lg:ml-[7px] lg:text-[13px]
                      xl:ml-[8px] xl:text-[17px]
                      2xl:ml-[9px] 2xl:text-[18px]
                      3xl:ml-[10px] 3xl:text-[20px]"
                >
                  {formatPriceNumberWithSymbol(productPriceInfo.price, currency)}
                </span>
              )}
            </div>
          </div>

          {/* 购买按钮 */}
          <div
            className="btn_word bg-primary-black text-white text-center font-bb cursor-pointer
                  md:w-[90px] md:h-[25px] md:leading-[27px] md:text-[10px]
                  lg:w-[120px] lg:h-[33px] lg:leading-[36px] lg:text-[12px]
                  xl:w-[150px] xl:h-[42px] xl:leading-[45px] xl:text-[14px]
                  2xl:w-[162px] 2xl:h-[45px] 2xl:leading-[48px] 2xl:text-[16px]
                  3xl:w-[180px] 3xl:h-[50px] 3xl:leading-[54px] 3xl:text-[16px]"
            onClick={(e) => handleBuyNowClick(e, product, index)}
          >
            {product.btn_word || 'Buy Now'}
          </div>
        </div>
      </li>
    );
  });

  ProductItem.displayName = 'ProductItem';

  if (!productListsTwo.length) return null;

  return (
    <div className="color-lists w-full" data-section="color-lists">
      {/* 按照Vue版本：flex space-between，平铺布局，无左右间距 */}
      <ul className="flex justify-between items-center flex-wrap w-full">
        {productListsTwo.map((product, index) => (
          <ProductItem key={index} product={product} index={index} />
        ))}
      </ul>
    </div>
  );
}
